import {FC} from "react";
import Badge from "../Badge";
import {MoveUpRight, LineChart as LineChartIcon} from "lucide-react";
import LineChart from "./LineChart";
import Card from "../Card";
import {CostDetailsData} from "@/@types/home";

interface CostDetailsProps {
  costDetailsData: CostDetailsData | undefined;
}

const CostDetails: FC<CostDetailsProps> = ({costDetailsData}) => {
  const EmptyMetrics = () => (
    <div className="flex flex-col justify-start items-start mr-5">
      <h3 className="text-[#8F95B2] font-semibold text-sm">
        Total Credits
      </h3>
      <h2 className="text-3xl font-bold inputoutput tracking-wide text-gray-300">
        0 credits
      </h2>
      <h4 className="text-sm font-bold inputoutput">
        Average per operation {" "}
        <Badge classColor="progress-gray" size="sm">
          <MoveUpRight className="mr-1 h-4 w-auto text-gray-400 text-xs inline"/>
          0 credits
        </Badge>
      </h4>
    </div>
  );
  
  const EmptyChart = () => (
    <div className="flex flex-col items-center justify-center h-80 w-full">
      <LineChartIcon className="w-16 h-16 text-gray-300 mb-4" />
      <p className="text-sm text-gray-500 text-center mb-2">
        No cost data available
      </p>
      <p className="text-xs text-gray-400 text-center max-w-[250px]">
        Cost statistics will appear here once transactions occur
      </p>
    </div>
  );
  
  return (
    <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="[510px]">
      <div className="p-5">
        <div className="flex justify-between items-center mb-4">
          <h1 className="flex justify-between items-center text-xl font-bold w-full max-w-[180px]">
            <img
              src="/icons/wallet.png"
              alt="Credits"
              width={25}
              height={25}
            />
            Credits Usage
          </h1>
        </div>
        
        {costDetailsData ? (
          <>
            <div className="flex justify-start items-center mb-4">
              <div className="flex flex-col justify-start items-start mr-5">
                <h3 className="text-[#8F95B2] font-semibold text-sm">
                  Total Credits Spent
                </h3>
                <h2 className="text-3xl font-bold inputoutput tracking-wide">
                  {costDetailsData.totalCoinsSpent.toLocaleString()} credits
                </h2>
                <h4 className="text-sm font-bold inputoutput">
                  Average per shred {" "}
                  <Badge classColor="progress-purple" size="sm">
                    <MoveUpRight className="mr-1 h-4 w-auto text-[#6F6AF8] text-xs inline"/>
                    {costDetailsData.avgCoinsPerShred.toLocaleString()} credits
                  </Badge>
                </h4>
              </div>
              <div className="flex flex-col justify-start items-start mr-5">
                <h3 className="text-[#8F95B2] font-semibold text-sm">
                  Total Credits Purchased
                </h3>
                <h2 className="text-3xl font-bold inputoutput tracking-wide">
                  {costDetailsData.totalCoinsPurchased.toLocaleString()} credits
                </h2>
                <h4 className="text-sm font-bold inputoutput">
                  Average per purchase {" "}
                  <Badge size="sm" classColor="progress-green">
                    <MoveUpRight className="mr-1 h-4 w-auto text-[#46d64b] text-xs inline"/>
                    {costDetailsData.avgPurchasedPerOp.toLocaleString()} credits
                  </Badge>
                </h4>
              </div>
            </div>
            <div className="mt-6 flex justify-stretch items-stretch h-80">
              <LineChart data={costDetailsData.data} colors={costDetailsData.colors}/>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-start items-center mb-4">
              <EmptyMetrics />
              <EmptyMetrics />
            </div>
            <EmptyChart />
          </>
        )}
      </div>
    </Card>
  );
};

export default CostDetails;